import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EmptyState } from '@vst/beam';
import { Alert } from '@vst/beam-icons/illustrative-icons';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { getClient } from '@app/services/graphqlClient';
import { SESSION_STORAGE_VARIABLES, sessionStorageManager } from '@app/services/storageManager';
import { useCanChangePlan, useCanPlaceOrders } from '@app/hooks';
import { refreshContext, selectChangePlan, selectLocale, selectPlanInfo, selectUser, selectUserInfo } from '@app/redux';
import { GET_HAS_PENDING_TRANSITION } from '@app/redux/changePlan/requests';
import { isVoiceOnlyPlan } from '@app/utils';

import { AddDataBoostSection } from '../../AddDataBoost';
import { BuyMoreHistory } from './buy-more-history';
import { Header } from './card-header';
import { DisconnectedContent } from './disconnected-content';
import { LoadingContent } from './loading';
import { PreinstallContent } from './preinstall-content';
import { VoiceContent } from './voice-content';

import styles from './styles.module.scss';

type Props = {
  id: string;
  isLoading: boolean;
  handleChangePlan?: () => void;
  handlePlanDetailsClick: () => void;
  handleNavigateToPlanPage: () => void;
  showFooter?: boolean;
  children?: React.ReactNode;
};

export const UsageSummaryCard = ({
  id,
  isLoading,
  handleChangePlan,
  handlePlanDetailsClick,
  showFooter,
  children,
}: Props) => {
  const dispatch = useDispatch();

  const { t } = useTranslation('Error');

  const {
    userInfo: { isPreInstall },
  } = useSelector(selectUserInfo);

  const { hasActivationError } = useSelector(selectChangePlan);

  const {
    errors: { usage: usageErrors },
    data: {
      characteristics: { id: productTypeId },
    },
  } = useSelector(selectPlanInfo);

  const {
    locale: { userLocale },
  } = useSelector(selectLocale);

  const {
    user: {
      auth: { tokenInfo },
    },
  } = useSelector(selectUser);

  const [client, setClient] = useState(getClient(tokenInfo, userLocale));

  useEffect(() => {
    setClient(getClient(tokenInfo, userLocale));
  }, [tokenInfo, userLocale]);

  const { isDisconnected, canPlaceOrders } = useCanPlaceOrders();
  const canChangePlan = useCanChangePlan();

  // According to: https://www.apollographql.com/docs/react/errors#%7B%22version%22%3A%223.14.0%22%2C%22message%22%3A104%2C%22args%22%3A%5B%22cache.diff%22%2C%22canonizeResults%22%2C%22Please%20remove%20this%20option.%22%5D%7D
  // Apollo recommends avoiding side effects in onCompleted and instead using useEffect to handle such logic.
  // the issue was likely related to side effects being triggered during the onCompleted callback of the useQuery hook.
  const { data } = useQuery(GET_HAS_PENDING_TRANSITION, {
    skip: canChangePlan || isPreInstall,
    pollInterval: 5 * 60 * 1000,
    fetchPolicy: 'network-only',
    client,
    variables: { refetchData: true },
  });

  useEffect(() => {
    if (data?.getHasPendingTransition?.isChangePlanDisabled === false) {
      sessionStorageManager.removeItem(SESSION_STORAGE_VARIABLES.CHANGE_PLAN_SUCCESS);
      dispatch(refreshContext());
    }
  }, [data, dispatch]);

  const showError = !isLoading && usageErrors;

  if (isPreInstall) {
    return (
      <div data-cy={id} className={`${styles['containerWhite']} usage-summary`}>
        <Header
          handleChangePlan={handleChangePlan}
          handlePlanDetailsClick={handlePlanDetailsClick}
          isChangePlanDisabled={!canChangePlan}
        />
        <PreinstallContent />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div data-cy={id} className={styles['containerWhite']}>
        <LoadingContent />
      </div>
    );
  }

  if (isDisconnected) {
    return (
      <div data-cy={id} className={`${styles['containerWhite']} usage-summary`}>
        <DisconnectedContent />
      </div>
    );
  }

  if (showError) {
    return (
      <div data-cy={id} className={styles['containerError']}>
        <EmptyState icon={Alert} title={t('title')} description={t('message', { error: t('usage_usagesummary') })} />
      </div>
    );
  }

  const hasOnlyVoicePlan = isVoiceOnlyPlan(productTypeId);

  if (hasOnlyVoicePlan) {
    return (
      <div data-cy={id} className={`${styles['containerWhite']} usage-summary`}>
        <Header
          handleChangePlan={handleChangePlan}
          handlePlanDetailsClick={handlePlanDetailsClick}
          isChangePlanDisabled={!canChangePlan}
        />
        <VoiceContent />
      </div>
    );
  }

  return (
    <div data-cy={id} className={`${styles['containerWhite']} usage-summary`}>
      <Header
        handleChangePlan={handleChangePlan}
        handlePlanDetailsClick={handlePlanDetailsClick}
        isChangePlanDisabled={!canChangePlan || hasActivationError}
      />

      {children}

      {showFooter && (
        <>
          <AddDataBoostSection />
          <BuyMoreHistory />
        </>
      )}
    </div>
  );
};
